import { useCallback, useContext } from 'react';

import type { UserPreferenceLocation } from '@cognite/apm-client';

import type { InFieldCDMLocationConfig } from '../cdm-types';

import { UseLocationsContext } from './cdm-use-locations.context';
import type { LocationOption } from './types';
import { mapLocationConfigToOption } from './utils/cdm-map-location-config-to-option';

export interface UseLocationsInterface {
  handleSaveLocation: (location?: UserPreferenceLocation) => Promise<void>;
  selectedLocationConfig?: InFieldCDMLocationConfig;
  allLocationConfigs: InFieldCDMLocationConfig[];
  isLoading: boolean;
  isError: boolean;
  selectedLocationOption?: LocationOption;
  locationOptions: LocationOption[];
}

export const useLocations = (): UseLocationsInterface => {
  const {
    useCurrentUserContext,
    useGetAllInfieldLocations,
    useUpsertCurrentUserPreferences,
  } = useContext(UseLocationsContext);

  const { user } = useCurrentUserContext();
  const { mutateAsync: upsertUserPreferences } =
    useUpsertCurrentUserPreferences();
  const {
    data: allLocationConfigs = [],
    isLoading,
    isError,
  } = useGetAllInfieldLocations();

  const userLocationPreference = user.preferences?.infield?.location;

  const selectedLocationConfig =
    userLocationPreference &&
    allLocationConfigs.find(
      (location) =>
        location.externalId === userLocationPreference.externalId &&
        location.space === userLocationPreference.space
    );

  const locationOptions = allLocationConfigs.map(mapLocationConfigToOption);

  const selectedLocationOption =
    selectedLocationConfig && mapLocationConfigToOption(selectedLocationConfig);

  const handleSaveLocation = useCallback(
    async (location?: UserPreferenceLocation) => {
      await upsertUserPreferences({
        preference: 'location',
        data: location,
      });
    },
    [upsertUserPreferences]
  );

  return {
    handleSaveLocation,
    selectedLocationConfig,
    allLocationConfigs,
    isLoading,
    isError,
    selectedLocationOption,
    locationOptions,
  };
};
